import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useMotionValueEvent, useReducedMotion, useScroll } from 'framer-motion';
import { ArrowRight, ChevronDown } from 'lucide-react';
import Header from './Header';
import { logEventSafe } from '../analytics/analytics';
import promoImage1 from '../assets/Ad1.jpg';
import promoImage2 from '../assets/Ad2.jpg';
import threeD from '../assets/3D.png';
import { COMPANY_SNAPSHOT, EXPLORE_LINKS, SUPPORT_EMAIL } from '../content/siteContent';

const REPO_OWNER = 'ToolipDev';
const REPO_NAME = 'PublicReleasesRepo';
const WIDGET_GROUPS = [
  { title: 'Focus', items: 'Pomodoro, Stopwatch, Countdown' },
  { title: 'Quick math', items: 'Calculator, percentages, time math' },
  { title: 'Organize', items: 'Links, notes, tables, calendars' },
  { title: 'Utility', items: 'System stats, file drop, media tools' },
];
const sectionViewport = { once: true, amount: 0.2 };

const LandingPage = () => {
  const [showScrollPrompt, setShowScrollPrompt] = useState(true);
  const [wordIndex, setWordIndex] = useState(0);
  const [maxWordWidth, setMaxWordWidth] = useState(0);
  const [latestExe, setLatestExe] = useState(null);
  const [loadingDownload, setLoadingDownload] = useState(true);
  const [downloadError, setDownloadError] = useState('');
  const measureRef = useRef(null);
  const { scrollY } = useScroll();
  const location = useLocation();
  const prefersReducedMotion = useReducedMotion();
  const words = ['Productivity', 'Organization', 'Momentum', 'Workflow'];

  useEffect(() => {
    const compute = () => {
      if (!measureRef.current) return;
      const spans = measureRef.current.querySelectorAll('[data-word]');
      let max = 0;
      spans.forEach((span) => {
        const width = span.getBoundingClientRect().width;
        if (width > max) max = width;
      });
      setMaxWordWidth(Math.ceil(max));
    };
    compute();
    window.addEventListener('resize', compute);
    document.fonts?.ready?.then(compute).catch(() => {});
    return () => window.removeEventListener('resize', compute);
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') setWordIndex((index) => (index + 1) % words.length);
    }, 4000);
    return () => clearInterval(id);
  }, [prefersReducedMotion, words.length]);

  useEffect(() => {
    logEventSafe('page_view', { page_path: location.pathname, page_title: document.title });
  }, [location]);

  useMotionValueEvent(scrollY, 'change', (latest) => {
    if (latest > 100 && showScrollPrompt) setShowScrollPrompt(false);
  });

  useEffect(() => {
    const fetchLatestRelease = async () => {
      try {
        const res = await fetch(`https://api.github.com/repos/${REPO_OWNER}/${REPO_NAME}/releases/latest`);
        if (!res.ok) throw new Error('Failed to fetch release information.');
        const data = await res.json();
        const exeAsset = data.assets.find((asset) => asset.name.toLowerCase().endsWith('.exe'));
        setLatestExe(exeAsset || null);
      } catch (err) {
        setDownloadError(err.message);
      } finally {
        setLoadingDownload(false);
      }
    };
    fetchLatestRelease();
  }, []);

  const handleDownloadClick = (placement) => {
    if (!latestExe) return;
    logEventSafe('download_click', { file_name: latestExe.name, placement, page_path: location.pathname });
  };

  const scrollDown = () => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
  const featuredLinks = EXPLORE_LINKS.slice(0, 4);
  const animatedWordWidth = maxWordWidth ? `${maxWordWidth + 20}px` : undefined;
  const fadeUp = {
    hidden: { opacity: 0, y: prefersReducedMotion ? 0 : 28 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: prefersReducedMotion ? 0.2 : 0.65, ease: [0.22, 1, 0.36, 1] },
    },
  };
  const staggerGroup = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: prefersReducedMotion ? 0.04 : 0.1,
        delayChildren: prefersReducedMotion ? 0 : 0.08,
      },
    },
  };
  const cardHover = prefersReducedMotion
    ? {}
    : {
        y: -6,
        borderColor: 'rgba(96, 165, 250, 0.28)',
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        transition: { duration: 0.22, ease: 'easeOut' },
      };
  const linkCardHover = prefersReducedMotion
    ? {}
    : {
        y: -6,
        transition: { duration: 0.22, ease: 'easeOut' },
      };
  const floatingPreview = prefersReducedMotion
    ? {}
    : {
        y: [0, -12, 0],
        rotate: [0, -1.2, 0, 1.2, 0],
        transition: { duration: 8, repeat: Infinity, ease: 'easeInOut' },
      };

  return (
    <div className="relative flex flex-col dark transition-colors duration-300 overflow-hidden unselectable" style={{ zIndex: 0, background: 'linear-gradient(180deg, #111827 0%, #030712 100%)' }}>
      <div style={{ position: 'absolute', inset: 0, zIndex: -2, pointerEvents: 'none', backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '24px 24px', opacity: 0.14 }} />
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ zIndex: -1, background: 'radial-gradient(circle at 50% 0%, rgba(37,99,235,0.22), transparent 30%), radial-gradient(circle at 85% 35%, rgba(14,165,233,0.12), transparent 24%), linear-gradient(180deg, rgba(17,24,39,0) 0%, rgba(3,7,18,0.2) 100%)' }} />
      <Header />

      <div className="sr-only absolute -z-10" aria-hidden="true">
        <div ref={measureRef} className="text-4xl sm:text-[4.1rem] xl:text-[3.6rem] 2xl:text-[4.7rem] font-bold leading-tight">
          {words.map((word) => <span key={word} data-word className="animate-gradient-text glow inline-block mr-2">{word}</span>)}
        </div>
      </div>

      <section className="pt-20 sm:pt-24 lg:pt-20 pb-8 sm:pb-10">
        <div className="min-h-[calc(100vh-5rem)] flex items-center">
          <div className="site-frame">
            <div className="mx-auto grid w-full max-w-[94rem] xl:grid-cols-[minmax(20rem,25rem)_minmax(28rem,37rem)] 2xl:grid-cols-[minmax(25rem,31rem)_minmax(34rem,45rem)] xl:justify-center gap-8 xl:gap-8 2xl:gap-10 items-center xl:-translate-y-5 2xl:-translate-y-8">
            <motion.div
              className="hidden xl:flex justify-center xl:justify-end order-2 xl:order-1"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
            >
              <div className="relative">
                <motion.div
                  aria-hidden="true"
                  className="absolute inset-x-[12%] top-[8%] bottom-[14%] rounded-full bg-blue-500/15 blur-3xl"
                  animate={prefersReducedMotion ? { opacity: 0.5 } : { opacity: [0.35, 0.55, 0.35], scale: [0.98, 1.04, 0.98] }}
                  transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
                />
                <motion.img
                  src={threeD}
                  alt="Toolip desktop preview"
                  className="relative h-auto w-full max-w-[22rem] xl:max-w-[23rem] 2xl:max-w-[36rem] object-contain pointer-events-none select-none"
                  style={{ filter: 'drop-shadow(0 20px 40px rgba(37,99,235,0.14))' }}
                  animate={floatingPreview}
                />
              </div>
            </motion.div>

            <motion.div className="order-1 xl:order-2 min-w-0" variants={staggerGroup} initial="hidden" animate="visible">
              <motion.h1 variants={fadeUp} className="text-4xl sm:text-[4.1rem] xl:text-[3.6rem] 2xl:text-[4.7rem] font-bold text-white leading-[1.08] unselectable">
                Boost your{' '}
                <span className="relative inline-flex items-end overflow-visible align-baseline pr-[0.16em]" style={{ minHeight: '1.38em', paddingBottom: '0.12em', width: animatedWordWidth, verticalAlign: 'baseline' }} aria-live="polite">
                  <AnimatePresence mode="popLayout" initial={false}>
                    <motion.span key={wordIndex} initial={prefersReducedMotion ? { opacity: 0 } : { rotateX: -90, y: '0.25em', opacity: 0 }} animate={prefersReducedMotion ? { opacity: 1 } : { rotateX: 0, y: 0, opacity: 1 }} exit={prefersReducedMotion ? { opacity: 0 } : { rotateX: 90, y: '-0.25em', opacity: 0 }} transition={{ duration: prefersReducedMotion ? 0.2 : 0.6 }} className="animate-gradient-text glow block will-change-transform leading-[1.14] pb-[0.1em]">{words[wordIndex]}</motion.span>
                  </AnimatePresence>
                </span>
              </motion.h1>
              <motion.p variants={fadeUp} className="mt-5 xl:mt-5 text-base sm:text-[1.18rem] xl:text-[1.16rem] 2xl:text-[1.38rem] text-gray-300 max-w-xl xl:max-w-[35rem] 2xl:max-w-[45rem] leading-relaxed">Toolip is a customizable desktop toolbelt that keeps everyday utilities in one place so your workflow stays fast, organized, and easy to return to.</motion.p>
              <motion.div variants={fadeUp} className="mt-7 xl:mt-8 flex flex-col sm:flex-row gap-4 sm:items-center">
                {latestExe ? <motion.a href={latestExe.browser_download_url} target="_blank" rel="noopener noreferrer" className="inline-flex w-full sm:w-auto items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl transition-colors" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={() => handleDownloadClick('hero')}>Download for Windows</motion.a> : <Link to="/get-started" className="inline-flex w-full sm:w-auto items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl transition-colors">Get Started</Link>}
                <Link to="/features" className="inline-flex w-full sm:w-auto items-center justify-center gap-2 border border-white/10 bg-white/5 hover:bg-white/10 text-white px-6 py-3 rounded-xl transition-colors">View features <ArrowRight className="h-4 w-4" /></Link>
              </motion.div>
              <div className="mt-10 sm:hidden rounded-2xl border border-white/10 bg-white/[0.04] divide-y divide-white/10 overflow-hidden">
                {COMPANY_SNAPSHOT.map((item) => <div key={item.label} className="flex items-start justify-between gap-4 px-4 py-3"><div className="text-[11px] uppercase tracking-[0.22em] text-gray-500">{item.label}</div><div className="text-right text-sm text-gray-200">{item.value}</div></div>)}
              </div>
              <motion.div variants={staggerGroup} className="mt-8 xl:mt-10 hidden sm:grid sm:grid-cols-3 gap-4 xl:gap-5 2xl:gap-6">
                {COMPANY_SNAPSHOT.map((item) => (
                  <motion.div key={item.label} variants={fadeUp} whileHover={cardHover} className="rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-4 2xl:px-6 2xl:py-5">
                    <div className="text-xs uppercase tracking-[0.25em] text-gray-500">{item.label}</div>
                    <div className="mt-2 text-sm text-gray-200">{item.value}</div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
            </div>
          </div>
        </div>
      </section>

      {showScrollPrompt && <motion.div className="fixed inset-x-0 bottom-5 z-40 flex justify-center cursor-pointer sm:bottom-7" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0, transition: { repeat: Infinity, repeatType: 'reverse', duration: 1.5 } }} onClick={scrollDown}><ChevronDown size={32} className="text-white drop-shadow-[0_4px_14px_rgba(0,0,0,0.45)]" /></motion.div>}

      <main className="pb-20 space-y-20">
        <motion.section
          className="site-frame"
          variants={staggerGroup}
          initial="hidden"
          whileInView="visible"
          viewport={sectionViewport}
        >
          <div className="sm:hidden rounded-2xl border border-white/10 bg-white/[0.04] divide-y divide-white/10 overflow-hidden">
            {WIDGET_GROUPS.map((group) => (
              <div key={group.title} className="px-4 py-3">
                <div className="text-[11px] uppercase tracking-[0.22em] text-blue-300">{group.title}</div>
                <p className="mt-1 text-sm leading-relaxed text-gray-300">{group.items}</p>
              </div>
            ))}
          </div>
          <div className="hidden gap-4 sm:grid sm:grid-cols-2 xl:grid-cols-4 2xl:gap-6">
            {WIDGET_GROUPS.map((group) => (
              <motion.div key={group.title} variants={fadeUp} whileHover={cardHover} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 2xl:p-7">
                <div className="text-sm uppercase tracking-[0.24em] text-blue-300">{group.title}</div>
                <p className="mt-3 text-gray-300 leading-relaxed">{group.items}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section
          className="site-frame space-y-16 2xl:space-y-20"
          variants={staggerGroup}
          initial="hidden"
          whileInView="visible"
          viewport={sectionViewport}
        >
          <div className="grid lg:grid-cols-[1.2fr_0.8fr] 2xl:grid-cols-[1.1fr_0.9fr] gap-10 2xl:gap-16 items-center">
            <motion.div variants={fadeUp} className="flex justify-center lg:justify-start">
              <motion.img src={promoImage1} alt="Toolip app graphic" className="w-full max-w-5xl 2xl:max-w-6xl h-auto object-contain rounded-2xl shadow-2xl" whileHover={prefersReducedMotion ? {} : { scale: 1.015, y: -4 }} transition={{ duration: 0.25 }} />
            </motion.div>
            <motion.div variants={fadeUp}>
              <div className="text-sm uppercase tracking-[0.3em] text-blue-300">Organized at a glance</div>
              <h2 className="mt-3 text-3xl sm:text-4xl 2xl:text-[2.85rem] font-bold text-white">Keep the tools you use most within reach</h2>
              <p className="mt-4 text-lg 2xl:text-xl text-gray-300 leading-relaxed max-w-2xl">Toolip brings together practical utilities, clean layouts, and customization options so your setup feels personal without becoming cluttered.</p>
            </motion.div>
          </div>

          <div className="grid lg:grid-cols-[0.8fr_1.2fr] 2xl:grid-cols-[0.9fr_1.1fr] gap-10 2xl:gap-16 items-center">
            <motion.div variants={fadeUp} className="order-2 lg:order-1">
              <div className="text-sm uppercase tracking-[0.3em] text-blue-300">Made to stay useful</div>
              <h2 className="mt-3 text-3xl sm:text-4xl 2xl:text-[2.85rem] font-bold text-white">Concise tools for everyday work</h2>
              <p className="mt-4 text-lg 2xl:text-xl text-gray-300 leading-relaxed max-w-2xl">From timers and calculators to link management and quick notes, Toolip is built around tools that are fast to open, easy to understand, and ready when you need them.</p>
            </motion.div>
            <motion.div variants={fadeUp} className="order-1 lg:order-2 flex justify-center lg:justify-end">
              <motion.img src={promoImage2} alt="Toolip interface graphic" className="w-full max-w-5xl 2xl:max-w-6xl h-auto object-contain rounded-2xl shadow-2xl" whileHover={prefersReducedMotion ? {} : { scale: 1.015, y: -4 }} transition={{ duration: 0.25 }} />
            </motion.div>
          </div>
        </motion.section>

        <motion.section
          className="site-frame"
          variants={staggerGroup}
          initial="hidden"
          whileInView="visible"
          viewport={sectionViewport}
        >
          <motion.div variants={fadeUp} className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
            <div>
              <div className="text-sm uppercase tracking-[0.3em] text-blue-300">Explore</div>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-white">Product, pricing, and support</h2>
            </div>
          </motion.div>
          <motion.div variants={staggerGroup} className="grid md:grid-cols-2 xl:grid-cols-4 gap-5 2xl:gap-6">
            {featuredLinks.map((item) => (
              <motion.div key={item.title} variants={fadeUp} whileHover={linkCardHover} className="h-full">
                <Link to={item.route} className="group flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.04] p-6 2xl:p-7 transition-colors hover:bg-white/[0.07] hover:border-blue-400/30">
                  <div className="text-xs uppercase tracking-[0.25em] text-gray-500">{item.eyebrow}</div>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <h3 className="text-xl font-semibold text-white">{item.title}</h3>
                    <motion.div whileHover={prefersReducedMotion ? {} : { x: 3 }} transition={{ duration: 0.2 }}>
                      <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-white transition-colors" />
                    </motion.div>
                  </div>
                  <p className="mt-3 flex-1 text-gray-300 leading-relaxed">{item.description}</p>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </motion.section>

        <motion.section
          className="site-frame desktop-page-width-xl"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={sectionViewport}
        >
          <motion.div whileHover={prefersReducedMotion ? {} : { y: -4, borderColor: 'rgba(96, 165, 250, 0.24)' }} className="rounded-2xl border border-white/10 bg-white/[0.04] p-8 md:p-10">
            <div className="text-sm uppercase tracking-[0.3em] text-blue-300">Updates</div>
            <div className="mt-3 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-white">Follow product releases and progress</h2>
                <p className="mt-2 text-gray-400">See the full update history on the dedicated release notes page.</p>
              </div>
              <Link to="/updates" className="inline-flex items-center gap-2 text-white hover:text-blue-300 transition-colors">See all updates <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </motion.div>
        </motion.section>

        <motion.section
          className="site-frame desktop-page-width-lg"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={sectionViewport}
        >
          <motion.div
            className="bg-gray-800/50 rounded-2xl shadow-lg ring-1 ring-white/10 p-8 flex flex-col md:flex-row items-center justify-between gap-6"
            animate={prefersReducedMotion ? {} : { boxShadow: ['0 10px 30px rgba(0,0,0,0.15)', '0 14px 36px rgba(37,99,235,0.12)', '0 10px 30px rgba(0,0,0,0.15)'] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <div className="text-center md:text-left">
              <div className="text-sm uppercase tracking-[0.25em] text-blue-300">Get started</div>
              <h4 className="mt-2 text-2xl font-semibold text-white leading-tight">Download Toolip and try it for yourself.</h4>
            </div>
            <div className="shrink-0">{loadingDownload ? <span className="text-gray-300 text-sm">Loading latest version...</span> : downloadError ? <span className="text-red-500 text-sm">{downloadError}</span> : latestExe ? <motion.a href={latestExe.browser_download_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-xl text-sm transition-colors" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={() => handleDownloadClick('bottom_cta')}>Download for Windows</motion.a> : <Link to="/get-started" className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-xl text-sm transition-colors">Get Started</Link>}</div>
          </motion.div>
        </motion.section>
      </main>

      <footer className="bg-gray-800/50 ring-1 ring-white/10"><div className="site-frame py-12"><div className="grid grid-cols-1 items-start gap-8 md:grid-cols-4 2xl:grid-cols-[2.4fr_1fr_1fr]"><div className="md:col-span-2 2xl:col-span-1"><Link to="/" className="text-xl font-bold animate-gradient-text bg-clip-text text-transparent">Toolip</Link><p className="mt-4 text-gray-300 max-w-md 2xl:max-w-xl">A personal desktop software project built, maintained, and supported by one independent developer.</p><p className="mt-4 text-sm text-gray-400">Questions, bugs, or business inquiries: {SUPPORT_EMAIL}</p></div><div className="self-start"><h3 className="text-sm font-semibold text-white uppercase tracking-wider">Product</h3><ul className="mt-4 space-y-3"><li><Link to="/features" className="text-base text-gray-300 hover:text-gray-100">Features</Link></li><li><Link to="/pricing" className="text-base text-gray-300 hover:text-gray-100">Pricing</Link></li><li><Link to="/updates" className="text-base text-gray-300 hover:text-gray-100">Updates</Link></li></ul></div><div className="self-start"><h3 className="text-sm font-semibold text-white uppercase tracking-wider">Company</h3><ul className="mt-4 space-y-3"><li><Link to="/about" className="text-base text-gray-300 hover:text-gray-100">About</Link></li><li><Link to="/contact" className="text-base text-gray-300 hover:text-gray-100">Contact</Link></li><li><Link to="/feedback" className="text-base text-gray-300 hover:text-gray-100">Feedback</Link></li><li><Link to="/policies" className="text-base text-gray-300 hover:text-gray-100">Privacy and Terms</Link></li></ul></div></div><div className="mt-8 border-t border-gray-700 pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"><p className="text-base text-gray-500">&copy; 2026 Toolip. All rights reserved.</p><p className="text-sm text-gray-500">{SUPPORT_EMAIL}</p></div></div></footer>
    </div>
  );
};

export default LandingPage;
